import React from 'react';
import { RedemptionMetrics } from '../../../services/analyticsService';
import Icon from '../../../components/AppIcon';

interface RedemptionKPICardsProps {
  metrics: RedemptionMetrics;
}

const RedemptionKPICards: React.FC<RedemptionKPICardsProps> = ({ metrics }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-AT', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num);
  };

  const kpiCards = [
    {
      title: 'Total Redemption Amount',
      value: formatCurrency(metrics.totalRedemptionAmount),
      icon: 'DollarSign',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      change: '+12.5%',
      changeType: 'positive' as const,
      description: 'Total amount paid by users after discount'
    },
    {
      title: 'Avg Redemption per User',
      value: formatNumber(metrics.averageRedemptionPerUser),
      icon: 'Users',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      change: '+8.3%',
      changeType: 'positive' as const,
      description: 'Total redemptions divided by total users'
    },
    {
      title: 'Total User Savings',
      value: formatCurrency(metrics.totalUserSavings),
      icon: 'PiggyBank',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      change: '+15.2%',
      changeType: 'positive' as const,
      description: 'Total savings by all users combined'
    },
    {
      title: 'Savings per User',
      value: formatCurrency(metrics.savingsPerUser),
      icon: 'TrendingUp',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      change: '+18.7%',
      changeType: 'positive' as const,
      description: 'Total user savings divided by total users'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {kpiCards.map((card, index) => (
        <div
          key={index}
          className={`bg-card border ${card.borderColor} rounded-lg p-6 hover:shadow-lg transition-all duration-200`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className={`p-2 ${card.bgColor} rounded-lg`}>
                  <Icon name={card.icon} size={20} className={card.color} />
                </div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  {card.title}
                </h3>
              </div>
              
              <div className="space-y-2">
                <p className="text-2xl font-bold text-foreground">
                  {card.value}
                </p>
                
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center space-x-1 text-sm ${
                    card.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <Icon 
                      name={card.changeType === 'positive' ? 'ArrowUp' : 'ArrowDown'} 
                      size={14} 
                    />
                    <span className="font-medium">{card.change}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">vs last period</span>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  {card.description}
                </p>
              </div>
            </div>
            
            {/* Trend Sparkline Placeholder */}
            <div className="hidden lg:block w-16 h-12 ml-4">
              <div className="w-full h-full bg-gradient-to-r from-transparent via-primary/20 to-primary/40 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RedemptionKPICards;